import React, { createContext, Suspense, useEffect, useState } from "react";
import "./App.css";

import LoaderPage from "./components/LoaderPage/LoaderPage";
import { userInformation } from "./interfaces/users";
import { getContext, saveContext } from "./utils/sessionStorage";
import RouterProv from "./router";
import AlertModal from "./components/AlertsModal/AlertsModal";
import BusinessProvider from "./components/BusinessProvider/BusinessProvider";
import VenueProvider from "./components/VenueProvider/VenueProvider";
import ErrorBoundary from "./components/Core/ErrorBoundary/ErrorBoundary";

export type ViewType = "venue" | "concession" | "pos" | "preparer" | "runner" | "consultation";

export interface UserSettingsFromDashboard {
  id_profile: number;
  viewType: ViewType;
  hasAdminAccess: boolean;
  name: string;
  inventoryAccess: boolean;
  canMarkExpiredCancelledSalesAsPaid: boolean;
  canChangeLanguage: boolean;
  cancellationApproverAccess: boolean;
  canManageVenueUsers: boolean;
  inventoryWarehouseUuids?: string[];
}

export interface AuthContextInterface {
  user: userInformation | null;
  isLogged: boolean;
  login: (user: userInformation) => void;
  getLogout: () => void;
  setProfileId: (n: number) => void;
  viewType: ViewType;
  changeViewType: (s: ViewType) => void;
  hasAdminAccess: boolean;
  setHasAdminAccess: (hasAdminAccess: boolean) => void;
  setCanMarkExpiredCancelledSalesAsPaid: (canMark: boolean) => void;
  setCanChangeLanguage: (canChange: boolean) => void;
  updateUserFromDashboard: (userSettings: UserSettingsFromDashboard) => void;
  inventoryAccess: boolean;
  canMarkExpiredCancelledSalesAsPaid: boolean;
  canChangeLanguage: boolean;
  cancellationApproverAccess: boolean;
  canManageVenueUsers: boolean;
}
interface AuthContextInformation {
  user: userInformation | null;
  isLogged: boolean;
  viewType: ViewType;
  hasAdminAccess: boolean;
  inventoryAccess: boolean;
  canMarkExpiredCancelledSalesAsPaid: boolean;
  canChangeLanguage: boolean;
  cancellationApproverAccess: boolean;
  canManageVenueUsers: boolean;
}
export const AuthContext = createContext<AuthContextInterface>({
  user: {
    token: "",
    uuid_user: "",
    id_profile: 0,
    name: "",
  },
  isLogged: false,
  login: () => null,
  getLogout: () => null,
  setProfileId: () => null,
  viewType: "venue",
  hasAdminAccess: false,
  changeViewType: () => null,
  setHasAdminAccess: () => null,
  setCanMarkExpiredCancelledSalesAsPaid: () => null,
  setCanChangeLanguage: () => null,
  updateUserFromDashboard: () => null,
  inventoryAccess: true,
  canMarkExpiredCancelledSalesAsPaid: false,
  canChangeLanguage: false,
  cancellationApproverAccess: false,
  canManageVenueUsers: false,
});

function App() {
  const [contextInfo, setContextInfo] = useState<AuthContextInformation>({
    isLogged: false,
    user: null,
    viewType: "venue",
    hasAdminAccess: false,
    inventoryAccess: true,
    canMarkExpiredCancelledSalesAsPaid: false,
    canChangeLanguage: false,
    cancellationApproverAccess: false,
    canManageVenueUsers: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const changeViewType = (s: ViewType) => {
    setContextInfo({
      ...contextInfo,
      viewType: s,
    });
  };

  //reload de la pagina
  useEffect(() => {
    const previousContext = getContext();
    if (previousContext !== null) {
      setContextInfo({
        user: previousContext.user,
        viewType: previousContext.viewType,
        isLogged: previousContext.isLogged,
        hasAdminAccess: previousContext.hasAdminAccess,
        inventoryAccess: previousContext.inventoryAccess,
        canMarkExpiredCancelledSalesAsPaid:
          previousContext.canMarkExpiredCancelledSalesAsPaid ?? false,
        canChangeLanguage: previousContext.canChangeLanguage ?? false,
        cancellationApproverAccess:
          previousContext.cancellationApproverAccess ?? false,
        canManageVenueUsers: previousContext.canManageVenueUsers ?? false,
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const updateUserFromDashboard = (userSettings: UserSettingsFromDashboard) => {
    setContextInfo((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        id_profile: userSettings.id_profile || 0,
        token: prev.user?.token || "",
        uuid_user: prev.user?.uuid_user || "",
        name: userSettings.name || "",
        inventoryWarehouseUuids:
          userSettings.inventoryWarehouseUuids !== undefined
            ? userSettings.inventoryWarehouseUuids
            : prev.user?.inventoryWarehouseUuids,
      },
      viewType: userSettings.viewType,
      hasAdminAccess: userSettings.hasAdminAccess,
      inventoryAccess: userSettings.inventoryAccess,
      canMarkExpiredCancelledSalesAsPaid:
        userSettings.canMarkExpiredCancelledSalesAsPaid,
      canChangeLanguage: userSettings.canChangeLanguage,
      cancellationApproverAccess: userSettings.cancellationApproverAccess,
      canManageVenueUsers: userSettings.canManageVenueUsers,
    }));
  };

  const updateContext = async () => {
    await saveContext({
      user: contextInfo.user,
      isLogged: contextInfo.isLogged,
      login: getLogin,
      getLogout,
      setProfileId,
      viewType: contextInfo.viewType,
      changeViewType,
      hasAdminAccess: contextInfo.hasAdminAccess,
      setHasAdminAccess: setHasAdminAccess,
      setCanMarkExpiredCancelledSalesAsPaid:
        setCanMarkExpiredCancelledSalesAsPaid,
      setCanChangeLanguage: setCanChangeLanguage,
      updateUserFromDashboard: updateUserFromDashboard,
      inventoryAccess: contextInfo.inventoryAccess,
      canMarkExpiredCancelledSalesAsPaid:
        contextInfo.canMarkExpiredCancelledSalesAsPaid,
      canChangeLanguage: contextInfo.canChangeLanguage,
      cancellationApproverAccess: contextInfo.cancellationApproverAccess,
      canManageVenueUsers: contextInfo.canManageVenueUsers,
    });
    setIsLoading(false);
  };

  //actualizar session Storage
  useEffect(() => {
    updateContext();
  }, [contextInfo]);

  const setProfileId = (n: number) => {
    if (contextInfo.user) {
      setContextInfo({
        ...contextInfo,
        user: {
          ...contextInfo.user,
          id_profile: n,
        },
      });
    }
  };

  const getLogin = (user: userInformation) => {
    setContextInfo({
      ...contextInfo,
      user: user,
      isLogged: true,
    });
  };

  const getLogout = () => {
    setContextInfo({
      user: null,
      isLogged: false,
      viewType: "venue",
      hasAdminAccess: false,
      inventoryAccess: true,
      canMarkExpiredCancelledSalesAsPaid: false,
      canChangeLanguage: false,
      cancellationApproverAccess: false,
      canManageVenueUsers: false,
    });
  };

  const setHasAdminAccess = (hasAdminAccess: boolean) => {
    setContextInfo((prev) => ({
      ...prev,
      hasAdminAccess: hasAdminAccess,
    }));
  };

  const setCanMarkExpiredCancelledSalesAsPaid = (canMark: boolean) => {
    setContextInfo((prev) => ({
      ...prev,
      canMarkExpiredCancelledSalesAsPaid: canMark,
    }));
  };

  const setCanChangeLanguage = (canChange: boolean) => {
    setContextInfo((prev) => ({
      ...prev,
      canChangeLanguage: canChange,
    }));
  };

  if (isLoading) {
    return <LoaderPage />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoaderPage />}>
        <AuthContext.Provider
          value={{
            user: contextInfo.user,
            isLogged: contextInfo.isLogged,
            login: getLogin,
            getLogout,
            setProfileId,
            viewType: contextInfo.viewType,
            changeViewType,
            hasAdminAccess: contextInfo.hasAdminAccess,
            setHasAdminAccess: setHasAdminAccess,
            setCanMarkExpiredCancelledSalesAsPaid:
              setCanMarkExpiredCancelledSalesAsPaid,
            setCanChangeLanguage: setCanChangeLanguage,
            updateUserFromDashboard: updateUserFromDashboard,
            inventoryAccess: contextInfo.inventoryAccess,
            canMarkExpiredCancelledSalesAsPaid:
              contextInfo.canMarkExpiredCancelledSalesAsPaid,
            canChangeLanguage: contextInfo.canChangeLanguage,
            cancellationApproverAccess: contextInfo.cancellationApproverAccess,
            canManageVenueUsers: contextInfo.canManageVenueUsers,
          }}
        >
          <BusinessProvider>
            <VenueProvider>
              <AlertModal>
                <RouterProv />
              </AlertModal>
            </VenueProvider>
          </BusinessProvider>
        </AuthContext.Provider>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
